import { ExternalServiceId, InternalId } from '@edgebox/data-definition-kit';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { extractReferenceKey, HealthCheckFormulaReference } from './GapAssignmentJoin';

export type HealthCheckReferenceData = {
  /** Whether Sync Core answered with the check; where it did not, every other value stands in for the check's own. */
  read: boolean;
  /** The name Sync Core gives the check, or its key where it gives none or cannot be read. */
  name: string;
  /** Whether `name` is the check's own name rather than its key standing in for it. */
  named: boolean;
  description?: string;
  defaultScoreWeight: number;
  formula?: HealthCheckFormulaReference;
  /** The stable keys of the issue types the check raises, which the issue list filters by. */
  issueTypeKeys: string[];
};

const healthCheckReferenceByKeyCache = new Map<string, HealthCheckReferenceData>();
const healthCheckReferenceByKeyRequestCache = new Map<string, Promise<HealthCheckReferenceData>>();

function normalizeDefaultScoreWeight(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 1;
  }

  return value;
}

function toReferenceKeyList(references: unknown): string[] {
  if (!Array.isArray(references)) {
    return [];
  }

  const keys: string[] = [];
  for (const reference of references) {
    const key = extractReferenceKey(reference);
    if (key && !keys.includes(key)) {
      keys.push(key);
    }
  }

  return keys;
}

function toStringList(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((value) => (typeof value === 'string' ? value.trim() : '')).filter((value): value is string => value.length > 0);
}

function toFormulaReference(healthCheck: unknown): HealthCheckFormulaReference | undefined {
  const formula = (healthCheck as { formula?: { parsed?: unknown } } | null | undefined)?.formula;
  if (!formula) {
    return undefined;
  }

  // Sync Core supplies structured `formula.parsed` term references; Embed reads them directly and
  // never mines the raw `expression` for `TERM(...)` calls.
  const parsed = (formula as { parsed?: { inputTerms?: unknown; outputTerms?: unknown; functionsUsed?: unknown } | null }).parsed;
  if (!parsed) {
    return undefined;
  }

  return {
    inputTermKeys: toReferenceKeyList(parsed.inputTerms),
    outputTermKeys: toReferenceKeyList(parsed.outputTerms),
    functionsUsed: toStringList(parsed.functionsUsed),
  };
}

export async function loadHealthCheckReferenceDataByKey(
  api: ISyncCoreApi | undefined,
  healthCheckKey: string
): Promise<HealthCheckReferenceData> {
  if (!api || !healthCheckKey) {
    return {
      read: false,
      name: healthCheckKey || 'Health check',
      named: false,
      defaultScoreWeight: 1,
      issueTypeKeys: [],
    };
  }

  const cached = healthCheckReferenceByKeyCache.get(healthCheckKey);
  if (cached) {
    return cached;
  }

  const pending = healthCheckReferenceByKeyRequestCache.get(healthCheckKey);
  if (pending) {
    return pending;
  }

  const request = api.governance.healthChecks
    .itemByKey(healthCheckKey as ExternalServiceId)
    .then((healthCheck) => ({
      read: !!healthCheck,
      name: healthCheck?.name?.trim() || healthCheckKey,
      named: !!healthCheck?.name?.trim(),
      description: healthCheck?.description?.trim() || undefined,
      defaultScoreWeight: normalizeDefaultScoreWeight(healthCheck?.defaultScoreWeight),
      formula: toFormulaReference(healthCheck),
      issueTypeKeys: toReferenceKeyList(healthCheck?.issueTypes),
    }))
    .catch(() => ({
      read: false,
      name: healthCheckKey,
      named: false,
      defaultScoreWeight: 1,
      issueTypeKeys: [],
    }))
    .finally(() => {
      healthCheckReferenceByKeyRequestCache.delete(healthCheckKey);
    });

  healthCheckReferenceByKeyRequestCache.set(healthCheckKey, request);

  const resolvedData = await request;
  // A check Sync Core did not answer with is asked for again next time, rather than standing in for the session.
  if (resolvedData.read) {
    healthCheckReferenceByKeyCache.set(healthCheckKey, resolvedData);
  }
  return resolvedData;
}

/** The weight of one revision of a check: the one a page's figure was computed with. */
export type HealthCheckRevisionWeight = {
  /** Whether Sync Core answered with the revision; where it did not, the revision has no weight here. */
  read: boolean;
  /** The weight the revision counts by in a page's health, as Sync Core reads it. */
  weight?: number;
};

/**
 * How long a page's checks wait for the weight of one revision. Every check's share is a part of the total of every
 * weight, so a read Sync Core never answers would keep every share waiting; once this is over, the revision counts
 * as not loaded, and the others' shares are taken over the weights that were.
 */
export const HEALTH_CHECK_WEIGHT_READ_MAX_MS = 10_000;

const weightByRevisionIdCache = new Map<string, HealthCheckRevisionWeight>();
const weightByRevisionIdRequestCache = new Map<string, Promise<HealthCheckRevisionWeight>>();

/**
 * The weight of the revision of a check a page's figure names, which is the weight the page's health was computed
 * with: a revision is never edited, so it is read once a session; one Sync Core did not answer with, or not within
 * `HEALTH_CHECK_WEIGHT_READ_MAX_MS`, is asked for again next time.
 */
export async function loadHealthCheckWeightByRevisionId(
  api: ISyncCoreApi | undefined,
  revisionId: string
): Promise<HealthCheckRevisionWeight> {
  if (!api || !revisionId) {
    return { read: false };
  }

  const cached = weightByRevisionIdCache.get(revisionId);
  if (cached) {
    return cached;
  }

  const pending = weightByRevisionIdRequestCache.get(revisionId);
  if (pending) {
    return pending;
  }

  const answered = api.governance.healthChecks
    .item(revisionId as InternalId)
    .then(
      (healthCheck): HealthCheckRevisionWeight =>
        healthCheck ? { read: true, weight: normalizeDefaultScoreWeight(healthCheck.defaultScoreWeight) } : { read: false }
    )
    .catch((): HealthCheckRevisionWeight => ({ read: false }));
  let expiry: ReturnType<typeof setTimeout> | undefined;
  const unanswered = new Promise<HealthCheckRevisionWeight>((resolve) => {
    expiry = setTimeout(() => resolve({ read: false }), HEALTH_CHECK_WEIGHT_READ_MAX_MS);
  });
  const request = Promise.race([answered, unanswered]).finally(() => {
    clearTimeout(expiry);
    weightByRevisionIdRequestCache.delete(revisionId);
  });

  weightByRevisionIdRequestCache.set(revisionId, request);

  const resolved = await request;
  if (resolved.read) {
    weightByRevisionIdCache.set(revisionId, resolved);
  }
  return resolved;
}
